import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import css from "./lesson.module.css";
import { trackDemoCompleted, trackVideoWatched } from "../../lib/analytics";
import { useTranslation } from "react-i18next";
import { getVideoCategories } from "../../lib/videoCategoryApi";
import { getLanguageLevelById, getLanguageLevels } from "../../lib/getLanguageLevels";
import { toggleVideoFavorite, getFavoriteStatus } from "../../lib/favoriteApi";
import { markSectionCompleted } from "../../lib/videoLessonApi";
const levelOrder = ["A1", "A2", "B1", "B2", "C1", "C2", "pre-A1"];
const levelMapping = {
    A1: {
        fullName: "Beginner",
        order: 1,
        bgColor: "#e8f5e9",
        textColor: "#2e7d32",
        name: "A1",
    },
    A2: {
        fullName: "Elementary",
        order: 2,
        bgColor: "#fff3e0",
        textColor: "#f57c00",
        name: "A2",
    },
    B1: {
        fullName: "Intermediate",
        order: 3,
        bgColor: "#e3f2fd",
        textColor: "#1565c0",
        name: "B1",
    },
    B2: {
        fullName: "Upper-Intermediate",
        order: 4,
        bgColor: "#ede7f6",
        textColor: "#673ab7",
        name: "B2",
    },
    C1: {
        fullName: "Advanced",
        order: 5,
        bgColor: "#ffebee",
        textColor: "#d32f2f",
        name: "C1",
    },
    C2: {
        fullName: "Proficiency",
        order: 6,
        bgColor: "#fce4ec",
        textColor: "#c2185b",
        name: "C2",
    },
    "pre-A1": {
        fullName: "Pre-Beginner",
        order: 0,
        bgColor: "#c8e6c9",
        textColor: "#1b5e20",
        name: "pre-A1",
    },
};

const WrapVideo = ({ lesson, authUser, videos, setVideos, onActivityComplete }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const videoRef = useRef(null);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedMenu, setShowSpeedMenu] = useState(false);
    const [lastTrackedSecond, setLastTrackedSecond] = useState(0);
    const [isLessonCompleted, setIsLessonCompleted] = useState(false);
    const [languageLevel, setLanguageLevel] = useState("");
    const [categories, setCategories] = useState([]);
    const [levels, setLevels] = useState([]);
    const { t } = useTranslation();
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [favoriteId, setFavoriteId] = useState(null);
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
    const videoSectionCompletedRef = useRef(false);

    const [levelStyles, setLevelStyles] = useState({
        bgColor: "#e0e0e0",
        textColor: "#000",
        fullName: "Unknown Level",
        order: "-",
    });
    const location = useLocation();

    useEffect(() => {
        const fetchLanguageLevel = async () => {
            try {
                const level = await getLanguageLevelById(lesson.video.languageLevel);

                setLanguageLevel(level.name);
                if (levelMapping[level.name]) {
                    setLevelStyles(levelMapping[level.name]);
                }
            } catch (error) {
                console.error("Error fetching language level:", error);
            }
        };

        const fetchFavoriteStatus = async () => {
            if (!authUser?.isAuthenticated) {
                setLiked(false);
                setFavoriteId(null);
                return;
            }

            try {
                const { isFavorited, favoriteId: favoriteIdValue } = await getFavoriteStatus(lesson.id);
                setLiked(isFavorited);
                setFavoriteId(favoriteIdValue);
            } catch (error) {
                console.error("Error fetching favorite status:", error);
                setLiked(lesson.is_favorited || false);
                setFavoriteId(lesson.favorite_id || null);
            }
        };

        if (lesson) {
            fetchLanguageLevel();
            fetchFavoriteStatus();

            // Check if video section is already completed from lesson data
            const videoProgress = lesson?.section_progress?.video;
            if (videoProgress && videoProgress.status === "completed") {
                videoSectionCompletedRef.current = true;
            }
        }
    }, [lesson, authUser?.isAuthenticated]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesData = await getVideoCategories();
                setCategories(categoriesData.results);
            } catch (error) {
                console.error("Error fetching categories:", error);
            }
        };

        const fetchLevels = async () => {
            try {
                const levelsData = await getLanguageLevels();
                const adaptedLevels = levelOrder
                    .map((key) => levelsData.results.find((level) => level.name === key))
                    .filter(Boolean)
                    .map((level) => ({
                        id: String(level.id),
                        name: levelMapping[level.name] || level.name,
                    }));
                setLevels([{ id: "all", name: "All levels" }, ...adaptedLevels]);
            } catch (error) {
                console.error("Error fetching levels:", error);
            }
        };

        fetchCategories();
        fetchLevels();
    }, []);

    const getCategoryName = (categoryId) => {
        const category = categories.find((cat) => cat.id === categoryId);
        return category ? category.name : "Unknown Category";
    };

    const getCategoryIcon = (categoryId) => {
        const parentCategory = findParentCategory(categoryId);
        if (!parentCategory) return "🌐";

        const name = parentCategory.name?.toLowerCase() || "";

        // Check for more specific categories first (order matters!)
        if (
            name.includes("talk") ||
            name.includes("dialogue") ||
            name.includes("діалог") ||
            name.includes("розмов") ||
            name.includes("спілкування") ||
            name.includes("rozmawiać") ||
            name.includes("dialog") ||
            name.includes("socializ")
        )
            return "🗣️";
        if (name.includes("all") || name.includes("всі") || name.includes("wszystkie")) return "🌐";
        if (name.includes("travel") || name.includes("подорож") || name.includes("podróż")) return "✈️";
        if (
            name.includes("work") ||
            name.includes("робот") ||
            name.includes("praca") ||
            name.includes("business") ||
            name.includes("бізнес")
        )
            return "💼";
        if (name.includes("education") || name.includes("освіт") || name.includes("edukacja") || name.includes("learn"))
            return "📚";
        if (name.includes("health") || name.includes("здоров") || name.includes("zdrowie")) return "❤️";
        if (
            name.includes("news") ||
            name.includes("новин") ||
            name.includes("wiadomości") ||
            name.includes("tech") ||
            name.includes("технолог")
        )
            return "📱";
        if (
            name.includes("leisure") ||
            name.includes("дозвілля") ||
            name.includes("rozrywka") ||
            name.includes("entertainment") ||
            name.includes("розваг")
        )
            return "🎮";
        if (name.includes("daily") || name.includes("щоден") || name.includes("codzienn")) return "📅";

        return "🌐";
    };

    const findParentCategory = (categoryId) => {
        const category = categories.find((cat) => cat.id === categoryId);
        if (!category) return null;

        if (category.parent === null) {
            return category;
        }

        return categories.find((cat) => cat.id === category.parent);
    };

    useEffect(() => {
        const savedSpeed = localStorage.getItem("videoSpeed");
        if (savedSpeed) {
            setPlaybackRate(parseFloat(savedSpeed));
        }
    }, []);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // Track video watching time (every 10 seconds to avoid too many events)
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            const currentTime = Math.floor(video.currentTime);
            // Track every 10 seconds to avoid too many events
            const shouldTrack = currentTime >= lastTrackedSecond + 10;

            if (shouldTrack && currentTime > lastTrackedSecond) {
                setLastTrackedSecond(currentTime);
                const lessonId = parseInt(id);
                if (lessonId) {
                    trackVideoWatched(lessonId, currentTime);
                }
            }
        };

        // Check completion when video ends
        const handleVideoEnded = async () => {
            console.log("Video ended, marking video section as completed...");

            // Mark video section as completed via API using LESSON ID (not video ID)
            if (!videoSectionCompletedRef.current && lesson?.id) {
                try {
                    await markSectionCompleted(lesson.id, "video", "completed");
                    videoSectionCompletedRef.current = true;
                    console.log("Video section marked as completed for lesson", lesson.id);
                } catch (error) {
                    console.error("Failed to mark video section as completed:", error);
                }
            }

            if (onActivityComplete) {
                // Wait for backend to process video completion
                setTimeout(() => {
                    onActivityComplete();
                }, 2000);
            }
        };

        // Wait for video to be ready before tracking
        const setupTracking = () => {
            video.addEventListener("timeupdate", handleTimeUpdate);
            video.addEventListener("ended", handleVideoEnded);
        };

        if (video.readyState >= 2) {
            // Video is already loaded
            setupTracking();
        } else {
            video.addEventListener("canplay", setupTracking, { once: true });
        }

        return () => {
            video.removeEventListener("timeupdate", handleTimeUpdate);
            video.removeEventListener("ended", handleVideoEnded);
            video.removeEventListener("canplay", setupTracking);
        };
    }, [id, lastTrackedSecond, onActivityComplete]);

    useEffect(() => {
        const currentVideo = videos.find((v) => v.id === lesson.id);
        const lessonCompleted = currentVideo?.is_completed || false;
        setIsLessonCompleted(lessonCompleted);
        console.log("Lesson completion status:", lessonCompleted);
    }, [lesson, videos]);

    const handleSpeedChange = (speed) => {
        setPlaybackRate(speed);
        setShowSpeedMenu(false);
        localStorage.setItem("videoSpeed", speed);
    };

    const handlePreviousLesson = () => {
        const currentId = parseInt(id);
        const previousId = currentId - 1;
        if (previousId > 0) {
            navigate(`/lesson/${previousId}`);
            window.location.reload();
        }
    };

    const handleNextLesson = () => {
        const currentId = parseInt(id);
        const nextId = currentId + 1;
        navigate(`/lesson/${nextId}`);
        window.location.reload();
    };

    const handleCompleteLesson = () => {
        const lessonId = parseInt(id);

        // Track demo completion if this is demo lesson (774)
        if (lessonId === 774) {
            trackDemoCompleted(lessonId);
        }

        console.log("Lesson completed!");
    };

    const setLike = async (val) => {
        if (val === 1) {
            if (isTogglingFavorite || !authUser?.isAuthenticated) return;

            setLiked(!liked);
            if (disliked) setDisliked(false);
            setIsTogglingFavorite(true);

            try {
                const response = await toggleVideoFavorite(lesson.id, favoriteId, liked);
                if (!liked) {
                    setFavoriteId(response.id);
                } else {
                    setFavoriteId(null);
                }
            } catch (error) {
                console.error("Error toggling favorite:", error);
                const status = await getFavoriteStatus(lesson.id);
                setLiked(status.isFavorited);
                setFavoriteId(status.favoriteId);
            } finally {
                setIsTogglingFavorite(false);
            }
        } else if (val === -1) {
            setDisliked(!disliked);
            if (liked) setLiked(false);
        }
    };

    return (
        <div className={css.videoWrap}>
            <div className={css.videoContainer}>
                <video
                    ref={videoRef}
                    controls
                    width="100%"
                    height="auto"
                    poster={lesson.thumbnail}
                    controlsList="nodownload"
                    disablePictureInPicture>
                    <source src={lesson.video.file} type="video/mp4" />
                    Ваш браузер не підтримує відтворення відео.
                </video>

                <div className={css.speedControl}>
                    <button className={css.speedButton} onClick={() => setShowSpeedMenu(!showSpeedMenu)}>
                        {playbackRate}x ▾
                    </button>

                    {showSpeedMenu && (
                        <div className={css.speedMenu}>
                            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
                                <div
                                    key={speed}
                                    className={`${css.speedOption} ${speed === playbackRate ? css.active : ""}`}
                                    onClick={() => handleSpeedChange(speed)}>
                                    {speed}x
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Controls 
      <div className={css.controlsSection}>
        <button className={css.btnControl} onClick={handlePreviousLesson}>
          <span>⏮</span>
          {t("Previous Lesson")}
        </button>
        <button
          className={`${css.btnControl} ${isLessonCompleted ? css.btnControlCompleted : css.btnControlNotCompleted}`}
          disabled
        >
          {isLessonCompleted ? `${t("Lesson Completed")} ✓` : `${t("Lesson Not Completed")} ✗`}
        </button>
        <button className={css.btnControl} onClick={handleNextLesson}>
          {t("Next Lesson")}
          <span>⏭</span>
        </button>
      </div>*/}
            <div className={css.videoBottom}>
                <div className={css.vbInfo}>
                    <div className={css.vbLevel}>
                        <span className={css.vbLevelDot}></span>
                        {t(levelStyles.fullName)}
                    </div>
                    <div className={css.vbTitle}>{lesson.video.name}</div>
                    <div className={css.vbTopic}>
                        {getCategoryIcon(lesson.video.category)} {getCategoryName(lesson.video.category)}
                    </div>
                </div>
                <div className={css.ctaArea} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {!location.pathname.includes("/trial-lesson") && (
                        <div className={css.vfeedback} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <button
                                className={`${css.vfbBtn} ${liked ? css.liked : ""}`}
                                onClick={() => setLike(1)}
                                title={t("Подобається")}>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M7 10v12" />
                                    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z" />
                                </svg>
                            </button>
                            <button
                                className={`${css.vfbBtn} ${disliked ? css.disliked : ""}`}
                                onClick={() => setLike(-1)}
                                title={t("Не подобається")}>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round">
                                    <path d="M17 14V2" />
                                    <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z" />
                                </svg>
                            </button>
                        </div>
                    )}
                    <div style={{ textAlign: "right" }}>
                        <button className={css.btnCta} onClick={() => window.advanceStep && window.advanceStep()}>
                            <span>{t("Далі: слова")}</span>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M5 12h14" />
                                <path d="m12 5 7 7-7 7" />
                            </svg>
                        </button>
                        <div className={css.ctaHint}>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <path d="m12 8 4 4-4 4" />
                                <path d="M8 12h8" />
                            </svg>
                            {t("Після перегляду переходьте до слів")}
                        </div>
                    </div>
                </div>
            </div>

            <p>{lesson.transcription}</p>
        </div>
    );
};

export default WrapVideo;
